import { Alert, Grid, List, ListItem, ListItemText, Typography } from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import React, { FunctionComponent, useState } from 'react'

import SimpleContainer from '../../../base/components/Container/SimpleContainer'
import Breadcrumb from '../../../base/components/Template/Breadcrumb/Breadcrumb'
import useOperaciones from '../../../base/hooks/useOperaciones'
import { notDanger } from '../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../utils/swal'
import { MonedaProps } from '../../moneda/interfaces'
import MonedaListadoDialog from '../../moneda/view/listado/MonedaListadoDialog'
import MonedaActualizarDialog from '../../moneda/view/upsert/MonedaActualizarDialog'
import { apiArticuloMonedaAdicional1Registro } from '../api/apiRegistroMonedaAdicional1'
import { apiArticuloMonedaAdicional2Registro } from '../api/apiRegistroMonedaAdicional2'
import { apiArticuloMonedaAdicional3Registro } from '../api/apiRegistroMonedaAdicional3'
import { apiArticuloMonedaPrimariaRegistro } from '../api/apiRegistroMonedaPrimaria'
import { articuloMonedaRoutesMap } from '../articuloMonedaRoutes'
import ArticuloMonedaCard from '../components/ArticuloMonedaCard'

interface OwnProps {}

type Props = OwnProps

/**
 * Gestion o paginba principal para gestionar los articulo Moneda
 * @constructor
 */
const ArticuloMonedaGestion: FunctionComponent<Props> = () => {
  const { articuloMoneda, refetchArticuloMoneda } = useOperaciones()
  const [openActualizarMoneda, setOpenActualizarMoneda] = useState(false)
  const [openRegistroMoneda, setOpenRegistroMoneda] = useState(false)
  const [tipoRegistro, setTipoRegistro] = useState<any>(-1)
  const [moneda, setMoneda] = useState<MonedaProps | null>(null)

  const confirm = useConfirm()

  /**
   * Registramos la moneda
   * @param data
   * @param tipo
   */
  const registrarMoneda = async (data: MonedaProps, tipo: number) => {
    const title = (
      <Typography variant={'subtitle1'}>
        Registro moneda {tipo === 0 ? 'local' : `adicional ${tipo}`}
      </Typography>
    )
    const message = (
      <Typography variant={'body2'}>
        Confirma que desea registrar la moneda{' '}
        <strong>
          {data.sigla} - {data.descripcion}
        </strong>
      </Typography>
    )
    const { confirmed } = await confirm({ title, content: message })
    if (!confirmed) return

    swalLoading()
    let resp: any = null
    switch (tipo) {
      case 0: {
        resp = await apiArticuloMonedaPrimariaRegistro(data.codigo).catch((e: any) => {
          swalException(e)
        })
        break
      }
      case 1: {
        resp = await apiArticuloMonedaAdicional1Registro(data.codigo).catch((e: any) => {
          swalException(e)
        })
        break
      }
      case 2: {
        resp = await apiArticuloMonedaAdicional2Registro(data.codigo).catch((e: any) => {
          swalException(e)
        })
        break
      }
      case 3: {
        resp = await apiArticuloMonedaAdicional3Registro(data.codigo).catch((e: any) => {
          swalException(e)
        })
        break
      }
      default: {
        notDanger('Error en upsert de monedas de sistema')
      }
    }
    if (resp) {
      setOpenRegistroMoneda(false)
      await refetchArticuloMoneda()
      setTipoRegistro(-1)
      swalClose()
    }
  }

  return (
    <>
      <SimpleContainer maxWidth={'md'}>
        <Breadcrumb routeSegments={[articuloMonedaRoutesMap.gestion]} />

        <Grid container spacing={3}>
          <Grid size={12}>
            <Alert color={'info'}>
              El sistema le permite añadir hasta 3 monedas adicionales.
              <List dense>
                <ListItem sx={{ p: 0 }}>
                  <ListItemText>
                    Debe existir siempre la moneda principal y su tipo de cambio Venta /
                    Compra debe ser siempre 1.
                  </ListItemText>
                </ListItem>
                <ListItem sx={{ p: 0 }}>
                  <ListItemText>
                    Para ELIMINAR una asociación de moneda, esta no debe contener ningún
                    movimiento de kardex.
                  </ListItemText>
                </ListItem>
                <ListItem sx={{ p: 0 }}>
                  <ListItemText>
                    Una Vez seleccionado una moneda principal, esta ya{' '}
                    <strong>no de podrá modificar ni eliminar.</strong>
                  </ListItemText>
                </ListItem>
              </List>
            </Alert>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ArticuloMonedaCard
              moneda={articuloMoneda.monedaPrimaria}
              monedaLocal={articuloMoneda.monedaPrimaria}
              subTitle={'Moneda Primaria'}
              editable={false}
              onRegistro={(resp?: boolean) => {
                if (resp) {
                  setOpenRegistroMoneda(true)
                  setTipoRegistro(0)
                }
              }}
              onEliminar={(resp?: MonedaProps) => {
                if (resp) {
                  console.log(resp)
                }
              }}
              onActualizar={(resp?: MonedaProps) => {
                if (resp) {
                  console.log(resp)
                }
              }}
            />
          </Grid>
          {articuloMoneda.monedaPrimaria && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ArticuloMonedaCard
                  moneda={articuloMoneda.monedaAdicional1}
                  monedaLocal={articuloMoneda.monedaPrimaria}
                  subTitle={'Moneda Adicional 1'}
                  editable={true}
                  onRegistro={(resp?: boolean) => {
                    if (resp) {
                      setOpenRegistroMoneda(true)
                      setTipoRegistro(1)
                    }
                  }}
                  onEliminar={(resp?: MonedaProps) => {
                    if (resp) {
                      console.log(resp)
                    }
                  }}
                  onActualizar={(resp?: MonedaProps) => {
                    if (resp) {
                      setMoneda(resp)
                      setOpenActualizarMoneda(true)
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ArticuloMonedaCard
                  moneda={articuloMoneda.monedaAdicional2}
                  monedaLocal={articuloMoneda.monedaPrimaria}
                  subTitle={'Moneda Adicional 2'}
                  editable={true}
                  onRegistro={(resp?: boolean) => {
                    if (resp) {
                      setOpenRegistroMoneda(true)
                      setTipoRegistro(2)
                    }
                  }}
                  onEliminar={(resp?: MonedaProps) => {
                    if (resp) {
                      console.log(resp)
                    }
                  }}
                  onActualizar={(resp?: MonedaProps) => {
                    if (resp) {
                      setMoneda(resp)
                      setOpenActualizarMoneda(true)
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ArticuloMonedaCard
                  moneda={articuloMoneda.monedaAdicional3}
                  monedaLocal={articuloMoneda.monedaPrimaria}
                  subTitle={'Moneda Adicional 3'}
                  editable={true}
                  onRegistro={(resp?: boolean) => {
                    if (resp) {
                      setOpenRegistroMoneda(true)
                      setTipoRegistro(3)
                    }
                  }}
                  onEliminar={(resp?: MonedaProps) => {
                    if (resp) {
                      console.log(resp)
                    }
                  }}
                  onActualizar={(resp?: MonedaProps) => {
                    if (resp) {
                      setMoneda(resp)
                      setOpenActualizarMoneda(true)
                    }
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </SimpleContainer>
      <MonedaActualizarDialog
        id={'ActualizarTipoCambio'}
        keepMounted={false}
        input={moneda}
        open={openActualizarMoneda}
        onClose={async (resp?: MonedaProps) => {
          if (resp) {
            await refetchArticuloMoneda()
          }
          setOpenActualizarMoneda(false)
          setMoneda(null)
        }}
      />
      <MonedaListadoDialog
        id={'MonedaListado'}
        keepMounted={false}
        open={openRegistroMoneda}
        onClose={async (resp?: MonedaProps) => {
          if (resp) {
            await registrarMoneda(resp, tipoRegistro)
          } else setOpenRegistroMoneda(false)
        }}
      />
    </>
  )
}

export default ArticuloMonedaGestion
